import { useMemo } from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import { Box } from "@mui/system";
import { t } from "i18next";
import { Autocomplete, TextField, Typography } from "@mui/material";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { formatNumber, fPercent } from "src/utils/format-number";
import ActivationCell from "../data-grid-campaign/components/ActivationCell";
import { formatCurrency } from "src/utils/format-currency";
import PercentageChange from "../components/PercentageChange";

export const useCampaignColumns = ({
    workspace,
    lang,
    selectedCompareDate,
    setConfirmDisable,
    setModalCreateStrategy,
}: any): GridColDef[] => {

    return useMemo(
        () => [
            {
                field: "campaign_name",
                sortable: true,
                disableColumnMenu: true,
                headerClassName: "keyword-header",
                headerName: "Campaign",
                flex: 1,
                minWidth: 260,

                renderHeader: () => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography
                            fontWeight={600}
                            color="rgba(99, 115, 129, 1)"
                            fontSize={14}
                        >
                            Campaign
                        </Typography>

                        <CustomTooltip title={"Ad clicks"}
                            placement="top"
                            arrow>
                            <Iconify
                                sx={{ cursor: 'help' }}
                                icon="proicons:cursor-click"
                                width={20}
                                height={20}
                                className="keyword-header-icon"
                            />
                        </CustomTooltip>
                    </Box>
                ),

                renderCell: ({ value, row }) => {
                    const values = Array.isArray(value)
                        ? value
                        : value
                            ? [value]
                            : [];

                    return (
                        <CustomTooltip
                            title={values.join(", ")}
                            placement="top"
                            arrow
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        px: "12px",
                                        py: "8px",
                                        borderRadius: "8px",
                                        backgroundColor: "#FCFCFD",
                                        boxShadow: "1px 3px 8px 0 rgba(0, 0, 0, 0.08)",
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    height: 60,
                                    px: "12px",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    boxSizing: "border-box",
                                    "&:hover": {
                                        backgroundColor: "#F7F8F9",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: "14px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        color: "rgba(51, 60, 71, 1)",
                                        cursor: "help",
                                    }}
                                >
                                    {(values || []).join(", ")}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        color: "rgba(99, 115, 129, 1)",
                                        fontVariantNumeric: "lining-nums tabular-nums",
                                        fontFamily: '"Public Sans", sans-serif',
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                        marginLeft: "12px",
                                    }}
                                >
                                    {formatNumber(row.clicks)}
                                </Typography>
                            </Box>
                        </CustomTooltip>
                    );
                },
            },
            {
                field: "campaign_id",
                type: 'number',
                align: 'left',
                maxWidth: 120,
                flex: 1,
                valueGetter: (params) => {
                    return String(params).replace(/\s+/g, '');
                },
                headerAlign: 'left',
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Campaign ID
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Campaign ID",
                sortable: false
            },
            {
                field: "campaign_type",
                type: 'string',
                headerAlign: 'left',
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Campaign Type
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Campaign Type",
                minWidth: 130,
                sortable: false
            },
            {
                field: "campaign_primary_status_reasons",
                type: 'string',
                align: 'left',
                maxWidth: 260,
                flex: 1,
                headerAlign: 'left',
                valueGetter: (params: any) => {
                    const value = params;

                    if (!value) return '';

                    const list = Array.isArray(value)
                        ? value
                        : JSON.parse(value.replace(/'/g, '"'));

                    return list
                        .map((item: any) =>
                            item
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (c: any) => c.toUpperCase())
                        )
                        .join(', ');
                },
                renderHeader: () => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Status
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Status",
                sortable: false
            },
            {
                field: "platform",
                headerName: "Platform",
                minWidth: 140,
                sortable: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Platform
                        </Typography>
                    </Box>
                ),
                type: "singleSelect",
                valueOptions: [
                    { value: "GOOGLE", label: "Google Ads" },
                    { value: "BING", label: "Bing Ads" },
                ],

                valueGetter: (params: any) => (params || "").toUpperCase(),

                renderCell: (params) => {
                    const map: any = {
                        GOOGLE: "Google Ads",
                        BING: "Bing Ads",
                    };

                    return map[params.value] || params.value;
                },
            },
            {
                field: "bidding_strategy_type",
                type: 'string',
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Bidding Strategy Type
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Bidding Strategy Type",
                maxWidth: 200,
                flex: 1,
                sortable: true,
                filterable: true,
            },
            {
                field: "mode",
                headerName: "Mode",
                disableColumnMenu: true,
                filterable: true,
                minWidth: 120,
                maxWidth: 120,
                width: 120,
                sortable: false,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                            flexDirection: "column"
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Mode
                        </Typography>
                    </Box>
                ),
                editable: false,
                filterOperators: [
                    {
                        label: "is any of",
                        value: "isAnyOf",
                        getApplyFilterFn: () => null,

                        InputComponent: (props) => {
                            const { item, applyValue } = props;

                            return (
                                <Autocomplete
                                    sx={{ mt: 2.5 }}
                                    multiple
                                    options={[
                                        "KO Bidding Manual"
                                    ]}
                                    value={item.value || []}
                                    onChange={(event, newValue) => {
                                        applyValue({
                                            ...item,
                                            value: newValue,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            placeholder="Select modes"
                                        />
                                    )}
                                    size="small"
                                />
                            );
                        },
                    },
                ],
                renderCell: (params) => (
                    <ActivationCell
                        params={params}
                        setConfirmDisable={setConfirmDisable}
                        setModalCreateStrategy={setModalCreateStrategy}
                    />
                )
            },
            {
                disableColumnMenu: true,
                field: "max_cpc",
                filterable: false,
                type: "string",
                sortable: false,
                headerAlign: 'left',
                headerName: "Set Max CPC",
                minWidth: 80,
                editable: true,


                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                        sx={{
                            whiteSpace: "normal",
                            lineHeight: 1.2,
                        }}
                    >
                        Set Max CPC
                    </Typography>
                ),

                renderEditCell: (params) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) =>
                            d.status === true &&
                            d.mode === "KO Bidding Manual"
                    );

                    const value =
                        koManual?.max_cpc ??
                        koManual?.max_cpc_portfolio_strategy ??
                        null;

                    if (!koManual) {
                        return (
                            <Box display="flex" width="100%" justifyContent="flex-end">
                                <Typography color="#333C47">-</Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                height: "100%",
                                px: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                defaultValue={value}
                                autoFocus
                                fullWidth
                                onChange={(event) => {
                                    let value = event.target.value;
                                    value = value.replace(",", ".");

                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: "max_cpc",
                                        value,
                                    });
                                }}
                                sx={{
                                    width: "100%",

                                    "& .MuiInputBase-root": {
                                        height: "36px",
                                        display: "flex",
                                        alignItems: "center",
                                    },

                                    "& input": {
                                        padding: "0 12px",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                    },

                                }}
                            />
                        </Box>
                    );
                },

                renderCell: (params: any) => {

                    const activations =
                        params.row.activations_details || [];

                    const activation = activations.find(
                        (d: any) =>
                            d.status === true &&
                            (
                                d.mode === "KO Bidding Manual" ||
                                d.mode?.startsWith(
                                    "KO Bidding Auto"
                                )
                            )
                    );

                    const isAuto =
                        activation?.mode?.startsWith(
                            "KO Bidding Auto"
                        );

                    const value = isAuto

                        // =========================
                        // KO BIDDING AUTO
                        // =========================
                        ? (
                            activation?.max_cpc_portfolio_strategy ??
                            null
                        )

                        // =========================
                        // KO BIDDING MANUAL
                        // =========================
                        : (
                            activation?.max_cpc ??
                            activation?.max_cpc_portfolio_strategy ??
                            null
                        );

                    if (!activation) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Typography color="rgba(145, 158, 171, 1)">
                                    -
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            onClick={() => {

                                // 🔥 AUTO NON EDITABLE
                                if (isAuto) return;

                                params.api.startCellEditMode({
                                    id: params.id,
                                    field: params.field,
                                });
                            }}
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",

                                cursor: isAuto
                                    ? "default"
                                    : "pointer",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: "100%",
                                    height: "65%",

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",

                                    px: isAuto ? 0 : 2,

                                    borderRadius: "6px",

                                    background:
                                        "rgba(255, 255, 255, 0.01)",

                                    fontSize: "0.875rem",

                                    lineHeight: 1,

                                    whiteSpace: "nowrap",

                                    boxSizing: "border-box",

                                    border: isAuto
                                        ? ""
                                        : "1px solid rgba(145, 158, 171, 0.20)",

                                    transition:
                                        "all 0.15s ease",

                                    "&:hover": isAuto
                                        ? {}
                                        : {
                                            border:
                                                "1px solid var(--color-azure-58, #338AF3)",

                                            boxShadow:
                                                "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                        },
                                }}
                            >
                                {isAuto ? (

                                    <Box
                                        sx={{
                                            display: "flex",
                                            width: "100%",
                                            height: "100%",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Typography color="#333C47" sx={{
                                            width: 50,
                                            textAlign: "right",
                                            fontVariantNumeric: "lining-nums tabular-nums",
                                            fontFamily: '"Public Sans", sans-serif',
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            whiteSpace: "nowrap",
                                        }}>
                                            {formatCurrency(
                                                value,
                                                workspace?.currency,
                                                lang,
                                                true,
                                                2
                                            )}
                                        </Typography>
                                    </Box>

                                ) : (

                                    formatCurrency(
                                        value,
                                        workspace?.currency,
                                        lang,
                                        true,
                                        2
                                    )

                                )}
                            </Box>
                        </Box>
                    );
                },
            },
            {
                disableColumnMenu: true,
                field: "target_impression_share_position",
                filterable: false,
                type: "string",
                sortable: false,
                headerAlign: "left",
                headerName: "Position",
                minWidth: 180,
                editable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                    >
                        Position
                    </Typography>
                ),
                renderCell: (params: any) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) =>
                            d.status === true &&
                            d.mode === "KO Bidding Manual"
                    );

                    const value = koManual?.target_impression_share_position ?? null;

                    const isDisabled =
                        params?.row?.bidding_strategy_type === "Target CPA" ||
                        params?.row?.bidding_strategy_type === "Maximize Conversion Value" ||
                        params?.row?.bidding_strategy_type === "Target ROAS";

                    if (!koManual) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Typography color="#333C47">-</Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            onClick={() => {
                                if (isDisabled) return;

                                params.api.startCellEditMode({
                                    id: params.id,
                                    field: params.field,
                                });
                            }}
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                cursor: isDisabled ? "default" : "pointer",
                            }}
                        >
                            {!isDisabled
                                ? <Box
                                    sx={{
                                        width: "100%",
                                        height: "65%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        px: 2,
                                        borderRadius: "6px",
                                        border: "1px solid rgba(145, 158, 171, 0.20)",
                                        backgroundColor: "rgba(255,255,255,0.02)",
                                        transition: "all 0.15s ease",
                                        userSelect: "none",

                                        minWidth: 0,
                                        "&:hover": {
                                            border: isDisabled
                                                ? "1px solid rgba(145, 158, 171, 0.20)"
                                                : "1px solid #338AF3",
                                            boxShadow: isDisabled
                                                ? "none"
                                                : "0 0 0 2px rgba(51, 138, 243, 0.25)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,

                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",

                                            display: "block",
                                            textAlign: "right",

                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {value ?? "-"}
                                    </Box>
                                </Box>
                                : <Box
                                    sx={{
                                        display: "flex",
                                        width: "100%",
                                        height: "100%",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <Typography color="#333C47">-</Typography>
                                </Box>
                            }
                        </Box>
                    );
                },
                renderEditCell: (params: any) => {
                    const isDisabled =
                        params?.row?.bidding_strategy_type === "Target CPA" ||
                        params?.row?.bidding_strategy_type === "Maximize Conversion Value" ||
                        params?.row?.bidding_strategy_type === "Target ROAS";

                    const options = [
                        "Anywhere on results page",
                        "Top of results page",
                        "Absolute top of results page",
                    ];

                    if (isDisabled) {
                        return (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#999",
                                    fontSize: "0.875rem",
                                }}
                            >
                                Not editable for this strategy
                            </Box>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                px: 1,
                            }}
                        >
                            <Autocomplete
                                freeSolo
                                fullWidth
                                options={options}
                                value={params.value ?? ""}
                                onChange={(_, newValue) => {
                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: params.field,
                                        value: newValue,
                                    });
                                }}
                                onInputChange={(_, newInputValue) => {
                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: params.field,
                                        value: newInputValue,
                                    });
                                }}
                                renderInput={(inputParams) => (
                                    <TextField
                                        {...inputParams}
                                        autoFocus
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            position: "relative",
                                            top: 10,
                                            width: "100%",
                                            "& .MuiOutlinedInput-root": {
                                                height: 38,
                                                display: "flex",
                                                alignItems: "center",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    );

                },
            },
            {
                disableColumnMenu: true,
                field: "target_impression_share",
                filterable: false,
                type: "string",
                sortable: false,
                headerAlign: 'left',
                headerName: "Target IS",
                minWidth: 80,
                editable: true,
                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                    >
                        Target IS
                    </Typography>
                ),
                renderEditCell: (params) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) =>
                            d.status === true &&
                            d.mode === "KO Bidding Manual"
                    );

                    const value =
                        koManual?.target_impression_share ??
                        null;

                    if (!koManual) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography color="#333C47">-</Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                height: "100%",
                                px: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                defaultValue={value}
                                autoFocus
                                fullWidth
                                onChange={(event) => {
                                    let value = event.target.value;
                                    value = value.replace(",", ".");

                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: "target_impression_share",
                                        value,
                                    });
                                }}
                                sx={{
                                    width: "100%",

                                    "& .MuiInputBase-root": {
                                        height: "36px",
                                        display: "flex",
                                        alignItems: "center",
                                    },

                                    "& input": {
                                        padding: "0 12px",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                    },

                                }}
                            />
                        </Box>
                    );
                },
                renderCell: (params: any) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) =>
                            d.status === true &&
                            d.mode === "KO Bidding Manual"
                    );

                    const value =
                        koManual?.target_impression_share ??
                        null;

                    if (!koManual) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Typography color="#333C47">-</Typography>
                            </Box>
                        );
                    }

                    const isDisabled =
                        params?.row?.bidding_strategy_type === "Target CPA" ||
                        params?.row?.bidding_strategy_type === "Maximize Conversion Value" ||
                        params?.row?.bidding_strategy_type === "Target ROAS"


                    return (
                        <Box
                            onClick={() => {
                                if (isDisabled) return;

                                params.api.startCellEditMode({
                                    id: params.id,
                                    field: params.field,
                                });
                            }}
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                cursor: isDisabled ? "default" : "pointer",

                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: "100%",
                                    height: "65%",

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    px: isDisabled ? 0 : 2,
                                    borderRadius: "6px",
                                    background: "rgba(255, 255, 255, 0.01)",
                                    fontSize: "0.875rem",
                                    lineHeight: 1,
                                    whiteSpace: "nowrap",
                                    boxSizing: "border-box",

                                    border: isDisabled
                                        ? ""
                                        : "1px solid rgba(145, 158, 171, 0.20)",

                                    transition: "all 0.15s ease",

                                    "&:hover": isDisabled
                                        ? {}
                                        : {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                        },
                                }}
                            >
                                {!isDisabled
                                    ? fPercent(value)
                                    : <Box
                                        sx={{
                                            display: "flex",
                                            width: "100%",
                                            height: "100%",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Typography color="#333C47">-</Typography>
                                    </Box>
                                }
                            </Box>
                        </Box>
                    );
                },
            },
            // TARGET CPA
            {
                disableColumnMenu: true,
                field: "target_cpa",
                filterable: false,
                type: "string",
                sortable: false,
                headerAlign: 'left',
                headerName: "Target CPA",
                minWidth: 80,
                editable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                    >
                        Target CPA
                    </Typography>
                ),

                renderEditCell: (params) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) =>
                            d.status === true &&
                            d.mode === "KO Bidding Manual"
                    );

                    const value =
                        koManual?.target_cpa ??
                        koManual?.target_cpa_portfolio_strategy ??
                        null;

                    if (!koManual) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography color="#333C47">-</Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                height: "100%",
                                px: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                defaultValue={value}
                                autoFocus
                                fullWidth
                                onChange={(event) => {
                                    let value = event.target.value;
                                    value = value.replace(",", ".");

                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: "target_cpa",
                                        value,
                                    });
                                }}
                                sx={{
                                    width: "100%",

                                    "& .MuiInputBase-root": {
                                        height: "36px",
                                        display: "flex",
                                        alignItems: "center",
                                    },

                                    "& input": {
                                        padding: "0 12px",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                    },

                                }}
                            />
                        </Box>
                    );
                },

                renderCell: (params: any) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) =>
                            d.status === true &&
                            d.mode === "KO Bidding Manual"
                    );

                    const value =
                        koManual?.target_cpa ??
                        koManual?.target_cpa_portfolio_strategy ??
                        null;

                    if (!koManual) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Typography color="#333C47">-</Typography>
                            </Box>
                        );
                    }

                    const isDisabled =
                        params?.row?.bidding_strategy_type === "Target ROAS" ||
                        params?.row?.bidding_strategy_type === "Target Impression Share" ||
                        params?.row?.bidding_strategy_type === "Maximize Conversion Value";

                    return (
                        <Box
                            onClick={() => {
                                if (isDisabled) return;

                                params.api.startCellEditMode({
                                    id: params.id,
                                    field: params.field,
                                });
                            }}
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                cursor: isDisabled ? "default" : "pointer",

                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: "100%",
                                    height: "65%",

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    px: isDisabled ? 0 : 2,
                                    borderRadius: "6px",
                                    background: "rgba(255, 255, 255, 0.01)",
                                    fontSize: "0.875rem",
                                    lineHeight: 1,
                                    whiteSpace: "nowrap",
                                    boxSizing: "border-box",

                                    border: isDisabled
                                        ? ""
                                        : "1px solid rgba(145, 158, 171, 0.20)",

                                    transition: "all 0.15s ease",

                                    "&:hover": isDisabled
                                        ? {}
                                        : {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                        },
                                }}
                            >
                                {!isDisabled
                                    ? formatCurrency(value, workspace?.currency, lang, true, 2)
                                    : <Box
                                        sx={{
                                            display: "flex",
                                            width: "100%",
                                            height: "100%",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Typography color="#333C47">-</Typography>
                                    </Box>
                                }
                            </Box>
                        </Box>
                    );
                },
            },
            // TARGET ROAS
            {
                disableColumnMenu: true,
                field: "target_roas",
                filterable: false,
                type: "string",
                sortable: false,
                headerAlign: 'left',
                headerName: "Target ROAS",
                minWidth: 110,
                editable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                    >
                        Target ROAS
                    </Typography>
                ),

                renderEditCell: (params) => {
                    const activations =
                        params.row.activations_details || [];

                    const activation = activations.find(
                        (d: any) =>
                            d.status === true &&
                            (
                                d.mode ===
                                "KO Bidding Manual" ||
                                d.mode?.startsWith(
                                    "KO Bidding Auto"
                                )
                            )
                    );

                    const isAuto =
                        activation?.mode?.startsWith(
                            "KO Bidding Auto"
                        );

                    const value = (() => {

                        // =========================
                        // KO BIDDING AUTO
                        // =========================
                        if (isAuto) {

                            if (
                                activation?.target_roas_portfolio_strategy !=
                                null
                            ) {
                                return (
                                    activation.target_roas_portfolio_strategy *
                                    100
                                );
                            }

                            return null;
                        }

                        // =========================
                        // KO BIDDING MANUAL
                        // =========================
                        if (
                            activation?.target_roas != null
                        ) {
                            return (
                                activation.target_roas * 100
                            );
                        }

                        if (
                            activation?.target_roas_portfolio_strategy !=
                            null
                        ) {
                            return (
                                activation.target_roas_portfolio_strategy *
                                100
                            );
                        }

                        return null;
                    })();

                    if (!activation) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography color="#333C47">
                                    -
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                height: "100%",
                                px: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                defaultValue={value}
                                autoFocus
                                fullWidth
                                onChange={(event) => {
                                    let value =
                                        event.target.value;

                                    value = value.replace(
                                        ",",
                                        "."
                                    );

                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: "target_roas",

                                        value:
                                            Number(value) /
                                            100,
                                    });
                                }}
                                sx={{
                                    width: "100%",

                                    "& .MuiInputBase-root":
                                    {
                                        height: "36px",
                                        display: "flex",
                                        alignItems:
                                            "center",

                                        color: "inherit",

                                        backgroundColor: "transparent",

                                        border: "1px solid rgba(145, 158, 171, 0.20)",

                                        borderRadius:
                                            "6px",
                                    },

                                    "& input": {
                                        padding: "0 12px",
                                        height: "100%",
                                        display: "flex",
                                        alignItems:
                                            "center",
                                    },
                                }}
                            />
                        </Box>
                    );
                },

                renderCell: (params: any) => {
                    const activations =
                        params.row.activations_details || [];

                    const activation = activations.find(
                        (d: any) =>
                            d.status === true &&
                            (
                                d.mode ===
                                "KO Bidding Manual" ||
                                d.mode?.startsWith(
                                    "KO Bidding Auto"
                                )
                            )
                    );

                    const isAuto =
                        activation?.mode?.startsWith(
                            "KO Bidding Auto"
                        );

                    const value =
                        params.value != null
                            ? params.value * 100
                            : (() => {

                                if (isAuto) {
                                    if (
                                        activation?.target_roas_portfolio_strategy != null
                                    ) {
                                        return (
                                            activation.target_roas_portfolio_strategy * 100
                                        );
                                    }

                                    return null;
                                }

                                if (activation?.target_roas != null) {
                                    return activation.target_roas * 100;
                                }

                                if (
                                    activation?.target_roas_portfolio_strategy != null
                                ) {
                                    return (
                                        activation.target_roas_portfolio_strategy * 100
                                    );
                                }

                                return null;
                            })();

                    const isDisabled =
                        params?.row?.bidding_strategy_type ===
                        "Target CPA" ||
                        params?.row?.bidding_strategy_type ===
                        "Maximize Conversions" ||
                        params?.row?.bidding_strategy_type ===
                        "Target Impression Share";

                    if (!activation) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent:
                                        "flex-end",
                                }}
                            >
                                <Typography color="#333C47">
                                    -
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            onClick={() => {
                                if (isDisabled) return;

                                params.api.startCellEditMode({
                                    id: params.id,
                                    field: params.field,
                                });
                            }}
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                cursor: isDisabled
                                    ? "default"
                                    : "pointer",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: "100%",
                                    height: "65%",

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "flex-end",

                                    px: isDisabled ? 0 : 2,

                                    borderRadius: "6px",

                                    background:
                                        "rgba(255, 255, 255, 0.01)",

                                    fontSize: "0.875rem",

                                    lineHeight: 1,

                                    whiteSpace: "nowrap",

                                    boxSizing: "border-box",

                                    border: isDisabled
                                        ? ""
                                        : "1px solid rgba(145, 158, 171, 0.20)",

                                    transition:
                                        "all 0.15s ease",

                                    "&:hover": isDisabled
                                        ? {}
                                        : {
                                            border: "1px solid var(--color-azure-58, #338AF3)",

                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                        },
                                }}
                            >
                                {!isDisabled ? (
                                    fPercent(value)
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            width: "100%",
                                            height: "100%",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "flex-end",
                                        }}
                                    >
                                        <Typography color="#333C47">
                                            -
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                },
            },
            {
                field: "max_cpc_2",
                sortable: true,
                headerAlign: "left",
                disableColumnMenu: true,
                filterable: true,
                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                        sx={{
                            whiteSpace: "normal",
                            lineHeight: 1.2,
                        }}
                    >
                        Max CPC Evol
                    </Typography>
                ),
                headerName: "Max CPC Evol",
                renderCell: ({ row }) => {
                    const currentMaxCpc = row.max_cpc_2 ?? 0;
                    const compareMaxCpc = row?.max_cpc_compare ?? 0;

                    let percentageChange = null;

                    if (compareMaxCpc > 0) {
                        percentageChange =
                            ((currentMaxCpc - compareMaxCpc) / compareMaxCpc) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(currentMaxCpc, workspace?.currency, lang, true, 2) ?? ""}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },
                minWidth: 144,
                type: "number",
            },
            {
                field: "cost",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Ad Cost
                    </Typography>
                ),

                headerName: "Ad Cost",

                renderCell: ({ row }) => {
                    const currentCost = row.cost ?? 0;
                    const compareCost = row?.cost_compare ?? 0;

                    let percentageChange = null;

                    if (compareCost > 0) {
                        percentageChange =
                            ((currentCost - compareCost) / compareCost) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(
                                    currentCost,
                                    workspace?.currency,
                                    lang,
                                    true,
                                    0
                                )}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse={true} />
                            </Box>
                        </Box>
                    );
                },
                minWidth: 144,
                type: "number",
            },
            // AD CLICKS
            {
                field: "clicks",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Ad Clicks
                    </Typography>
                ),

                headerName: "Ad Clicks",

                renderCell: ({ row }) => {
                    const currentClicks = row.clicks ?? 0;
                    const compareClicks = row?.clicks_compare ?? 0;

                    let percentageChange = null;

                    if (compareClicks > 0) {
                        percentageChange =
                            ((currentClicks - compareClicks) / compareClicks) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#333C47",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatNumber(currentClicks)}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} />
                            </Box>
                        </Box>
                    );
                },

                minWidth: 144,
                type: "number",
            },
            // AVG. CPC
            {
                field: "avg_cpc",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Avg. CPC
                    </Typography>
                ),

                headerName: "Avg. CPC",

                renderCell: ({ row }) => {
                    const currentAvgCPC = row.avg_cpc ?? 0;
                    const compareAvgCPC = row?.avg_cpc_compare ?? 0;

                    let percentageChange = null;

                    if (compareAvgCPC > 0) {
                        percentageChange =
                            ((currentAvgCPC - compareAvgCPC) / compareAvgCPC) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(
                                    currentAvgCPC,
                                    workspace?.currency,
                                    lang,
                                    true,
                                    2
                                )}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },

                minWidth: 144,
                type: "number",
            }
        ], [workspace?.currency, lang, selectedCompareDate]);

};